import React, { FC, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, IconButton, InputLabel, List, ListItem, MenuItem, Paper, Select } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useWorkflowSteps } from 'context/workflow-steps-context';
import { useDepartments } from 'context/departments-context';
import { Department, DepartmentWorkflowStep } from 'models/organization';
import { WorkFlow } from 'models/workflow-step';
import { usePlanningTemplate } from 'hooks/usePlanningTemplate';
import { useValueTemplate } from 'hooks/useValueTemplate';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

type DepartmentWorkflowStepsProps = {
    department?: Department;
    onStepsChange: (steps: DepartmentWorkflowStep[]) => void;
};
const DepartmentWorkflowSteps: FC<DepartmentWorkflowStepsProps> = ({ department, onStepsChange }) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow', 'actions-materials']);
    const { workFlow } = useWorkflowSteps();
    const { departments } = useDepartments();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { planningTemplates } = usePlanningTemplate();
    const { valueTemplates } = useValueTemplate();
    const allTemplates: { workflowSteps: { _id?: string; stepId?: string; departmentId?: string }[] }[] = [
        ...planningTemplates,
        ...valueTemplates,
    ];

    const [selectedSteps, setSelectedSteps] = useState<DepartmentWorkflowStep[]>(department?.workflowSteps || []);
    const [addStepId, setAddStepId] = useState<string>('');
    const assignedStepIds = new Set<string>();

    departments.forEach((dept) => {
        // Skip current department so its steps remain selectable
        if (dept._id === department?._id) return;

        dept.workflowSteps?.forEach((step) => {
            assignedStepIds.add(step._id);
        });
    });

    const handleDeleteStep = (stepId: string) => {
        const isStepUsed = allTemplates.some((template) => template.workflowSteps?.some((step) => step._id === stepId));

        if (isStepUsed) {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('actions-materials:STEP_IN_USE_BY_PRODUCTION_TEMPLATES'),
            });
            return;
        }

        setSelectedSteps((prev) => {
            const updatedSteps = prev
                .filter((step) => step._id !== stepId)
                .map((step, index) => ({
                    ...step,
                    stepOrder: index,
                }));

            onStepsChange(updatedSteps);
            return updatedSteps;
        });
    };

    const handleAddStep = (stepId: string) => {
        if (!stepId) return;

        // Prevent duplicates early
        if (selectedSteps.some((s) => s._id === stepId)) return;

        const step = (workFlow as WorkFlow).steps.find((s) => s._id === stepId);
        if (!step) return;

        const updatedSteps = [
            ...selectedSteps,
            {
                _id: step._id as string,
                name: step.name,
                stepOrder: selectedSteps.length,
            },
        ];

        setSelectedSteps(updatedSteps);
        onStepsChange(updatedSteps);
        setAddStepId('');
    };

    const handleDragEnd = ({ source, destination }: DropResult) => {
        if (!destination || source.index === destination.index) return;

        const reordered = [...selectedSteps];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);

        const updatedSteps = reordered.map((step, index) => ({
            ...step,
            stepOrder: index,
        }));

        setSelectedSteps(updatedSteps);
        onStepsChange(updatedSteps);
    };

    return (
        <Box>
            <Paper variant="outlined">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="workflow-steps-droppable">
                        {(provided) => (
                            <List dense ref={provided.innerRef} {...provided.droppableProps}>
                                {selectedSteps.map((step, idx: number) => (
                                    <Draggable key={step._id} draggableId={step._id} index={idx}>
                                        {(dragProvided, dragSnapshot) => (
                                            <ListItem
                                                key={step._id}
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                secondaryAction={
                                                    <IconButton onClick={() => handleDeleteStep(step._id)} edge="end">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                                sx={{
                                                    background: dragSnapshot.isDragging ? '#f3f3f3' : undefined,
                                                    mb: 1,
                                                }}
                                            >
                                                <Box
                                                    {...dragProvided.dragHandleProps}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mr: 2,
                                                        cursor: 'grab',
                                                        color: dragSnapshot.isDragging
                                                            ? 'primary.main'
                                                            : 'action.active',
                                                    }}
                                                >
                                                    <DragIndicatorIcon />
                                                </Box>
                                                <Box flexGrow={1}>{step.name}</Box>
                                            </ListItem>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                <ListItem key={'add-step-list-item'}>
                                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                        <InputLabel id="add-step-label">
                                            {translate('lab-settings:ADD_STEP')}
                                        </InputLabel>
                                        <Select
                                            labelId="add-step-label"
                                            value={addStepId}
                                            label={translate('lab-settings:ADD_STEP')}
                                            onChange={(e) => handleAddStep(e.target.value as string)}
                                        >
                                            {(workFlow as WorkFlow).steps
                                                .filter((step) => {
                                                    const isAlreadySelected = selectedSteps.some(
                                                        (s) => s._id === step._id,
                                                    );
                                                    const isAssignedToAnotherDept = assignedStepIds.has(
                                                        step._id as string,
                                                    );

                                                    return !isAlreadySelected && !isAssignedToAnotherDept;
                                                })
                                                .map((step) => (
                                                    <MenuItem key={step._id} value={step._id}>
                                                        {step.name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </ListItem>
                            </List>
                        )}
                    </Droppable>
                </DragDropContext>
            </Paper>
        </Box>
    );
};

export default DepartmentWorkflowSteps;
